/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The agency's team, for the screens that size seats by who is in them. */

import { useCallback, useEffect, useState } from 'react';
import { AgenciesService } from '@/services/requests/housien-services/AgenciesService';
import type { TeamOverview } from '@/services/requests/housien-services/AgenciesService.team';

/**
 * Reads the whole team, and reloads it on demand.
 *
 * A failure keeps the last answer rather than clearing to an empty team: a
 * transient one must not read as "everybody left", and the seat controls
 * would offer seats that are in use.
 *
 * @param agencyId - The agency, or '' before it is known.
 * @returns The team and a way to read it again.
 */
export function useAgencyTeam(agencyId: string) {
  const [team, setTeam] = useState<TeamOverview | null>(null);

  const reload = useCallback(async () => {
    if (!agencyId) return;
    try {
      setTeam(await AgenciesService.getAgencyTeam(agencyId));
    } catch {
      // Keeps what it had; see above.
    }
  }, [agencyId]);

  useEffect(() => { reload(); }, [reload]);

  return { team, reload };
}
